import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, ThumbsUp, ThumbsDown, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import HelpFeedbackDialog from "./HelpFeedbackDialog";
import ContactSupportCard from "./ContactSupportCard";
import { HelpArticle } from "@/types/help";

interface HelpArticleCardProps {
  article: HelpArticle;
  defaultExpanded?: boolean;
}

const HelpArticleCard = ({ article, defaultExpanded = false }: HelpArticleCardProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [userVote, setUserVote] = useState<boolean | null>(null);
  const [isLoadingVote, setIsLoadingVote] = useState(true);

  // Check for existing vote on mount
  useEffect(() => {
    const checkExistingVote = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setIsLoadingVote(false);
        return;
      }

      const { data: existingVote } = await supabase
        .from('help_feedback' as any)
        .select('was_helpful')
        .eq('article_id', article.id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingVote && 'was_helpful' in existingVote) {
        setHasVoted(true);
        setUserVote(existingVote.was_helpful as boolean);
      }
      
      setIsLoadingVote(false);
    };

    checkExistingVote();
  }, [article.id]);

  const handleExpand = async () => {
    if (!isExpanded) {
      // Track view count when expanding
      const { error } = await supabase
        .from('help_articles' as any)
        .update({ view_count: article.view_count + 1 })
        .eq('id', article.id);
      
      if (error) console.error('Error tracking view:', error);
    }
    setIsExpanded(!isExpanded);
  };

  const handleFeedback = async (wasHelpful: boolean) => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      toast.error("Please sign in to provide feedback");
      return;
    }

    if (!wasHelpful) {
      setShowFeedbackDialog(true);
      return;
    }

    // Check if updating existing vote or creating new one
    if (hasVoted) {
      const { error: updateError } = await supabase
        .from('help_feedback' as any)
        .update({ was_helpful: wasHelpful })
        .eq('article_id', article.id)
        .eq('user_id', user.id);

      if (updateError) {
        toast.error("Failed to update feedback");
        return;
      }

      setUserVote(wasHelpful);
      toast.success("Your feedback has been updated!");
    } else {
      const { error: insertError } = await supabase
        .from('help_feedback' as any)
        .insert({
          article_id: article.id,
          user_id: user.id,
          was_helpful: wasHelpful,
        });

      if (insertError) {
        toast.error("Failed to submit feedback");
        return;
      }

      setHasVoted(true);
      setUserVote(wasHelpful);
      toast.success("Thank you for your feedback!");
    }
  };

  const handleShare = () => {
    const url = `https://godyspo.com/help#${article.id}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  /**
   * SECURITY NOTE: Safe use of dangerouslySetInnerHTML
   * 
   * This renders help article content with basic markdown formatting.
   * 
   * Security: This is SAFE because:
   * 1. Help articles can ONLY be created by service_role (admins)
   * 2. RLS policy prevents non-admin INSERT/UPDATE/DELETE
   * 3. Public users can only SELECT (read-only)
   * 
   * RLS Policy: service_role only for modifications
   * Location: supabase/migrations/20251107054732_587b7cf4-47e5-4b79-be7a-48a6a3acc861.sql
   * 
   * ⚠️ WARNING: If RLS policies change to allow user-generated content,
   * replace this with a proper markdown library (react-markdown) or add
   * DOMPurify sanitization immediately.
   * 
   * Current rendering: Simple regex replacement for:
   * - Bold text: **text** → <strong>text</strong>
   * - List items: - item → <li>item</li>
   */
  const renderAnswer = (text: string) => {
    // Simple markdown-like rendering for admin-controlled content
    return text.split('\n').map((line, i) => {
      // Handle bold
      line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      // Handle lists
      if (line.trim().startsWith('-')) {
        return <li key={i} className="ml-4" dangerouslySetInnerHTML={{ __html: line.substring(1).trim() }} />;
      }
      return <p key={i} className="mb-2" dangerouslySetInnerHTML={{ __html: line }} />;
    });
  };

  return (
    <>
      <Card id={article.id} className="bg-card border-border">
        <CardHeader className="cursor-pointer" onClick={handleExpand}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Badge variant="outline" className="mb-2">
                {article.category}
              </Badge>
              <CardTitle className="text-lg font-semibold text-foreground">
                {article.question}
              </CardTitle>
            </div>
            <Button variant="ghost" size="sm" className="shrink-0">
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent className="space-y-6">
            <div className="prose prose-invert max-w-none text-muted-foreground">
              {renderAnswer(article.answer)}
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Was this helpful?</span>
                <Button
                  variant={userVote === true ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleFeedback(true)}
                  disabled={isLoadingVote}
                  className="gap-1"
                >
                  <ThumbsUp className="h-3 w-3" />
                  <span className="text-xs">{article.helpful_count}</span>
                </Button>
                <Button
                  variant={userVote === false ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleFeedback(false)}
                  disabled={isLoadingVote}
                  className="gap-1"
                >
                  <ThumbsDown className="h-3 w-3" />
                  <span className="text-xs">{article.not_helpful_count}</span>
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={handleShare} className="gap-1">
                <Share2 className="h-3 w-3" />
                <span className="text-xs">Share</span>
              </Button>
            </div>
            <ContactSupportCard />
          </CardContent>
        )}
      </Card>
      <HelpFeedbackDialog
        open={showFeedbackDialog}
        onOpenChange={setShowFeedbackDialog}
        articleId={article.id}
        onSubmit={() => {
          setHasVoted(true);
          setUserVote(false);
        }}
        hasExistingVote={hasVoted}
      />
    </>
  );
};

export default HelpArticleCard;
